/**
 * The single role module for the twin and attention work (twin slice 1,
 * contract C0; plan §4.1 item 5, conflict X11).
 *
 * Every role SET that slice 1 reads lives here, so the copies that grew
 * across `App.tsx`, `SideNav`, `TopBar`, `TopBarStats` and `NoSiteNotice`
 * stop being the place a decision is made. C0 only freezes the sets:
 * existing call sites are NOT rewired by this file, and each workstream
 * moves its own call sites onto it. `ALERTS_ROLES` is therefore spelled
 * exactly as the `/alerts` guard spells it today, so swapping the guard
 * onto it changes nothing.
 *
 * PREFIX NORMALISATION. The SPA's canonical spelling is the
 * `nexsoss:`-prefixed `RoleToken` (`extractRoles` adds the prefix to bare
 * claim keys). The api-server speaks BARE names (`policy/capabilities.py`)
 * and is what will put roles into `AlertOut.ackedByRoles`/`ackedByRole`.
 * Every function here accepts either spelling, and every constant is
 * spelled as a `RoleToken`. A string that is neither a role nor a
 * prefixed role is ignored, never guessed at.
 */
import { ALL_ROLES, type RoleToken } from './types';

const PREFIX = 'nexsoss:';

type Bare<T> = T extends `nexsoss:${infer R}` ? R : never;

/** A role as the api-server spells it: `platform_admin`, not
 *  `nexsoss:platform_admin`. */
export type BareRole = Bare<RoleToken>;

/** Either spelling, or anything a payload might carry in its place. */
export type RoleLike = string | null | undefined;

const KNOWN: ReadonlySet<string> = new Set<string>(ALL_ROLES);

/** The `RoleToken` for either spelling of a known role; null otherwise.
 *  Matching is exact: `Platform_Admin` and ` platform_admin` are not
 *  roles, and neither is a key under another prefix. */
export function normaliseRole(role: RoleLike): RoleToken | null {
  if (typeof role !== 'string' || role === '') return null;
  const full = role.startsWith(PREFIX) ? role : `${PREFIX}${role}`;
  return KNOWN.has(full) ? (full as RoleToken) : null;
}

/** Known roles only, de-duplicated, in first-seen order. */
export function normaliseRoles(roles: Iterable<RoleLike> | null | undefined): RoleToken[] {
  const out: RoleToken[] = [];
  if (!roles) return out;
  for (const role of roles) {
    const token = normaliseRole(role);
    if (token && !out.includes(token)) out.push(token);
  }
  return out;
}

/** The api-server's spelling of a role. */
export function bareRole(role: RoleToken): BareRole {
  return role.slice(PREFIX.length) as BareRole;
}

/** True when any of `roles` (either spelling) is in `allowed`. */
export function hasAnyRole(
  roles: Iterable<RoleLike> | null | undefined,
  allowed: readonly RoleToken[],
): boolean {
  return normaliseRoles(roles).some((r) => allowed.includes(r));
}

/**
 * Who may open the Alerts screen and see the top bar's Alerts chip.
 *
 * The `/alerts` RoleGuard in `App.tsx`, the Alerts entry in `SideNav` and
 * `OPERATOR_ROLES` in `TopBarStats` all spell this same list today.
 * Deliberately NOT widened to the support roles in C0: the api-server
 * already admits them read-only to the alert list, but whether a support
 * login reaches this console with a site at all is not established
 * (plan §2.2 L9), and widening a live gate is not a contract freeze.
 */
export const ALERTS_ROLES: readonly RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:site_supervisor',
  'nexsoss:service_operator',
];

/**
 * Who is admitted to the Building view of the Overview (the locate
 * resolver's "role not admitted" fallback reason, plan W4 step 7).
 *
 * The four roles the `/twin` guard admits today, plus `support_l2` and
 * `support_l3` (Decision 15: support admitted read-only). The support
 * roles hold no device or task capability in this console, so the
 * drawers the view opens render read-only for them without any
 * twin-specific branch.
 */
export const BUILDING_VIEW_ROLES: readonly RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:site_supervisor',
  'nexsoss:foh_operator',
  'nexsoss:service_operator',
  'nexsoss:support_l2',
  'nexsoss:support_l3',
];

/**
 * The showcase account's role (Decision 19): it keeps the demo layers
 * and the bare `/twin` route.
 *
 * NOT a read-only role, whatever older comments say. `service_operator`
 * holds `door.open`/`door.close` and the full `lift.*` set
 * (`policy/capabilities.py`), and some hotel staff hold it (Q3).
 */
export const DEMO_ROLE: RoleToken = 'nexsoss:service_operator';

/**
 * Roles whose acknowledgement may read "Picked up by Nexsoss" (Q3).
 *
 * NOT `service_operator`: some hotel staff hold it, so an ack under that
 * role proves nothing about who picked the alert up.
 */
export const NEXSOSS_STAFF_ROLES: readonly RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:support_l2',
  'nexsoss:support_l3',
];

/** Roles that read the hotel's words: no codes, no "RMF", no simulated
 *  robots (Q4: `site_supervisor` is a site audience).
 *
 *  `service_operator` is here too (decided 2026-10-04): hotel staff hold
 *  it (Q3), so it reads the hotel's words and never sees the simulated
 *  fleet. It is still the demo role and still holds the lift and door
 *  verbs; this decides wording and visibility only. */
export const SITE_AUDIENCE_ROLES: readonly RoleToken[] = [
  'nexsoss:foh_operator',
  'nexsoss:site_supervisor',
  'nexsoss:service_operator',
];

/** Every other known role: Nexsoss's own. With `SITE_AUDIENCE_ROLES` it
 *  partitions `ALL_ROLES`, and it is the same list as
 *  `NEXSOSS_STAFF_ROLES`. */
export const OPS_AUDIENCE_ROLES: readonly RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:support_l2',
  'nexsoss:support_l3',
];

export type Audience = 'site' | 'ops';

/**
 * Which words and layers a person gets.
 *
 * `ops` when they hold ANY ops-audience role: an account that is
 * Nexsoss's as well as the hotel's reads Nexsoss's words. Otherwise
 * `site` -- `service_operator` included, though it holds the lift verbs,
 * because hotel staff hold it (decided 2026-10-04) -- which
 * is also the answer for a token with no known role at all: the hotel
 * wording claims nothing about what the reader can do, and hides the
 * simulated fleet.
 */
export function audienceFor(roles: Iterable<RoleLike> | null | undefined): Audience {
  return hasAnyRole(roles, OPS_AUDIENCE_ROLES) ? 'ops' : 'site';
}

/** Whether simulated (mock) robots, their toggle and their SIM tags are
 *  shown. Ops screens only; a site audience never sees them. */
export function canSeeSimulated(roles: Iterable<RoleLike> | null | undefined): boolean {
  return audienceFor(roles) === 'ops';
}

/** Whether these roles (an acker's, typically) belong to Nexsoss staff. */
export function isNexsossStaff(roles: Iterable<RoleLike> | null | undefined): boolean {
  return hasAnyRole(roles, NEXSOSS_STAFF_ROLES);
}
